import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ShadeDialog";
import { getOrLoadStickerDesign } from './StickerDesigns';
import { StickerDesign, Board } from '../utils/Primitives';
import { getStickerUrl } from './StickerDesigns';
import { displayDateForUI } from '../utils/GeneralUtilities';
import { getPaymentMethod } from '../payments/PaymentMethods';
import AddressDisplay from '../components/AddressDisplay';
import { Address, formatEther } from 'viem';
import GetTheLink from '../components/GetTheLink';
import PublishingReviewer from './PublishingReviewer';
import { useAccount } from 'wagmi';
import { pridi } from '../fonts';
import StickerSharePanel from './StickerSharePanel';

type StickerReviewerProps = {
  stickerId: number | null;
  boardRef: React.MutableRefObject<Board>;
  onClose: () => void;
  handleStickerChoice?: (sticker: StickerDesign) => void;
  handleReviewPlayer?: (playerAddress: Address) => void;
  showActions?: boolean;
  showPublisherLink?: boolean;
  openExternalUrl?: (url: string) => void;
};

export default function StickerReviewer({
  stickerId,
  boardRef,
  onClose,
  handleStickerChoice,
  handleReviewPlayer,
  showActions = true,
  showPublisherLink = true,
  openExternalUrl,
}: StickerReviewerProps) {
  const { address: playerAddress } = useAccount();
  const [sticker, setSticker] = useState<StickerDesign | null>(null);
  const [paymentMethodSymbol, setPaymentMethodSymbol] = useState<string | null>(null);
  const [showPublishingReviewer, setShowPublishingReviewer] = useState<boolean>(false);
  const [showSharePanel, setShowSharePanel] = useState<boolean>(false);

  useEffect(() => {
    if (stickerId !== null) {
      getOrLoadStickerDesign({ boardRef, stickerId }).then(setSticker);
    }
  }, [stickerId, boardRef]);

  useEffect(() => {
    setShowSharePanel(false);
  }, [stickerId]);

  useEffect(() => {
    console.log(`sticker: ${sticker}`);
    console.log(`sticker.paymentMethodId: ${sticker?.paymentMethodId}`);
    if (sticker && sticker.paymentMethodId !== undefined) {
      const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: sticker.paymentMethodId });
      setPaymentMethodSymbol(paymentMethod?.symbol || 'ETH');
    }
  }, [sticker, boardRef]);

  const handleAddressClick = (address: Address) => {
    if (!handleReviewPlayer) {
      return;
    }
    console.log(`handleAddressClick: ${address}`);
    handleReviewPlayer(address);
  };

  const handleOpenPublishingReviewer = () => {
    setShowPublishingReviewer(true);
  };

  const handleClosePublishingReviewer = () => {
    setShowPublishingReviewer(false);
  };

  if (stickerId === null) {
    return null; // Hide the dialog if no stickerId is provided
  }

  const isPublisher = Boolean(
    playerAddress &&
    sticker?.currentPublisher &&
    playerAddress.toLowerCase() === sticker.currentPublisher.toLowerCase()
  );

  const openExternalLink = (url: string) => {
    if (openExternalUrl) {
      openExternalUrl(url);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Dialog open={stickerId !== null && !showSharePanel} onOpenChange={onClose}>
        <DialogContent className='bg-white text-slate-900 p-4'>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle className={`text-2xl ${pridi.className}`}>{sticker?.name}</DialogTitle>
              {sticker && <GetTheLink placeId={null} slapId={null} stickerDesignId={sticker.id} />}
            </div>
            {sticker && (
              <div className="flex flex-col items-center space-y-4">
                <div className="mt-4 max-w-[256px] max-h-[256px] overflow-hidden rounded-lg shadow-lg"
                     style={{
                       width: `${256 * Math.min(1, sticker.aspectRatio)}px`,
                       height: `${256 * Math.min(1, 1/sticker.aspectRatio)}px`
                     }}>
                  <img
                    src={getStickerUrl(sticker)}
                    alt={sticker.name || 'Sticker'}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-4 w-full max-w-md">
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div className="flex flex-col gap-3">
                      {sticker.description && (
                        <div className="text-sm text-gray-600 break-words">{sticker.description}</div>
                      )}
                      {sticker.url && (
                        <div className="text-sm text-gray-600">
                          <a
                            href={sticker.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                            onClick={(event) => {
                              event.preventDefault();
                              openExternalLink(sticker.url!);
                            }}
                          >
                            {sticker.url}
                          </a>
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <p className="text-sm text-gray-600">Sticker ID: {sticker.id}</p>
                        <p className="text-sm text-gray-600">Price: {formatEther(BigInt(sticker.price))} {paymentMethodSymbol}</p>
                        <p className="text-sm text-gray-600">Slaps: {sticker.slapCount}</p>
                        <p className="text-sm text-gray-600">Published: {displayDateForUI(new Date(sticker.publishedAt))}</p>
                        <p className="text-sm text-gray-600">Aspect Ratio: {Number(sticker.aspectRatio).toFixed(2)}</p>
                      </div>
                      {showActions && isPublisher && (
                        <button
                          type="button"
                          className="bg-gray-100 rounded-lg px-3 py-2 text-left text-sm text-gray-600 shadow-md hover:scale-105 transition-transform"
                          onClick={handleOpenPublishingReviewer}
                        >
                          Sticker Settings
                        </button>
                      )}
                      {showPublisherLink ? (
                        sticker.originalPublisher && sticker.originalPublisher !== sticker.currentPublisher && (
                          <button
                            type="button"
                            className="bg-gray-100 rounded-lg px-3 py-2 text-left text-sm text-gray-600 shadow-md hover:scale-105 transition-transform"
                            onClick={() => handleAddressClick(sticker.originalPublisher as Address)}
                          >
                            Original Publisher:&nbsp;
                            <AddressDisplay address={sticker.originalPublisher} ensName={sticker.originalPublisherEns} />
                          </button>
                        )
                      ) : (
                        sticker.originalPublisher && sticker.originalPublisher !== sticker.currentPublisher && (
                          <p className="text-sm text-gray-600">
                            Original Publisher:&nbsp;
                            <AddressDisplay address={sticker.originalPublisher} ensName={sticker.originalPublisherEns} />
                          </p>
                        )
                      )}
                    </div>
                    <div className="flex flex-col gap-3">
                      {showActions && (
                        <button
                          onClick={() => handleStickerChoice?.(sticker)}
                          className="px-3 py-1.5 text-sm whitespace-nowrap bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Pick This Sticker
                        </button>
                      )}
                      {showPublisherLink ? (
                        <button
                          type="button"
                          className="bg-gray-100 rounded-lg px-3 py-1.5 text-left text-xs text-gray-600 shadow-md hover:scale-105 transition-transform break-all"
                          onClick={() => handleAddressClick(sticker.currentPublisher as Address)}
                        >
                          Publisher:&nbsp;
                          <AddressDisplay address={sticker.currentPublisher} ensName={sticker.currentPublisherEns} />
                        </button>
                      ) : (
                        <p className="text-sm text-gray-600 break-all">
                          Publisher:&nbsp;
                          <AddressDisplay address={sticker.currentPublisher} ensName={sticker.currentPublisherEns} />
                        </p>
                      )}
                      {showActions && isPublisher && (
                        <button
                          type="button"
                          className="bg-gray-100 rounded-lg px-3 py-1.5 text-left text-xs text-gray-600 shadow-md hover:scale-105 transition-transform"
                          onClick={() => setShowSharePanel(true)}
                        >
                          Share Sticker
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {showActions && isPublisher && (
        <Dialog open={showSharePanel} onOpenChange={() => setShowSharePanel(false)}>
          <DialogContent className="bg-white text-slate-900 p-4">
            <DialogHeader>
              <DialogTitle className="text-xl">Share Sticker</DialogTitle>
              <div className="mt-2 w-full max-w-md">
                <StickerSharePanel
                  stickerId={sticker?.id ?? null}
                  stickerName={sticker?.name}
                  isOpen={showSharePanel}
                  onClose={() => setShowSharePanel(false)}
                />
              </div>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}

      {showActions && showPublishingReviewer && (
        <PublishingReviewer
          stickerId={stickerId}
          boardRef={boardRef}
          onClose={handleClosePublishingReviewer}
        />
      )}
    </>
  );
}
